// client/src/components/Achievements/AchievementsSkeleton.jsx
import React from "react";

const AchievementsSkeleton = () => (
  <div className="space-y-8 animate-pulse" aria-busy="true" aria-label="Loading achievements">
    {/* Hero Banner Skeleton */}
    <div className="rounded-3xl p-8 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 border border-slate-200 relative overflow-hidden flex flex-col md:flex-row items-center gap-8 shadow-sm">
      {/* Avatar Circle */}
      <div className="relative shrink-0">
        <div className="w-24 h-24 rounded-full bg-slate-300 border-4 border-slate-200 shadow-inner"></div>
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-14 h-4 bg-slate-300 rounded-full"></div>
      </div>

      {/* Main Info */}
      <div className="flex-1 w-full space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div className="space-y-2">
            <div className="h-7 bg-slate-300 rounded-lg w-48"></div>
            <div className="h-4 bg-slate-300/70 rounded-md w-36"></div>
          </div>
          <div className="h-6 bg-slate-300 rounded-md w-28 self-start sm:self-auto"></div>
        </div>

        {/* XP Bar */}
        <div className="w-full h-3 bg-slate-300/70 rounded-full overflow-hidden"></div>
        <div className="h-3 bg-slate-300/60 rounded w-32 ml-auto"></div>
      </div>
    </div>

    {/* Rank Progression Card Skeleton */}
    <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-8">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-slate-200"></div>
        <div className="h-4 bg-slate-200 rounded w-36"></div>
      </div>

      <div className="relative py-2">
        <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 rounded-full"></div>
        <div className="relative flex justify-between items-center px-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-slate-200 border-4 border-white shadow-xs"></div>
              <div className="h-3 bg-slate-200 rounded w-14"></div>
              <div className="h-2.5 bg-slate-100 rounded w-10"></div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Main Grid: Left 2 cols, Right 1 col */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column: Daily Quests & Trophy Room */}
      <div className="lg:col-span-2 space-y-6">
        {/* Daily Quests Skeleton */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-slate-200"></div>
              <div className="h-5 bg-slate-200 rounded w-32"></div>
            </div>
            <div className="h-4 bg-slate-100 rounded w-24"></div>
          </div>

          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="border border-slate-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3.5 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-slate-200 shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/5"></div>
                    <div className="h-2.5 bg-slate-100 rounded w-4/5"></div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1.5"></div>
                  </div>
                </div>
                <div className="w-20 h-8 bg-slate-200 rounded-xl shrink-0 self-end sm:self-center"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Trophy Room Skeleton */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-slate-200"></div>
              <div className="h-5 bg-slate-200 rounded w-36"></div>
            </div>
            <div className="h-4 bg-slate-100 rounded w-28"></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="border border-slate-100 rounded-2xl p-4 flex flex-col items-center text-center space-y-2.5 bg-slate-50/40"
              >
                <div className="w-14 h-14 rounded-2xl bg-slate-200"></div>
                <div className="h-4 bg-slate-200 rounded w-20"></div>
                <div className="h-3 bg-slate-100 rounded w-28"></div>
                <div className="w-full h-1.5 bg-slate-200 rounded-full mt-2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column: Leaderboard Skeleton */}
      <div className="space-y-6">
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-2">
            <div className="h-5 bg-slate-200 rounded w-28"></div>
            <div className="w-16 h-7 bg-slate-100 rounded-lg"></div>
          </div>

          {/* Tabs */}
          <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
            <div className="h-7 bg-white rounded-lg flex-1 shadow-xs"></div>
            <div className="h-7 bg-slate-100 rounded-lg flex-1"></div>
          </div>

          {/* User Sticky Rank Card */}
          <div className="h-16 bg-amber-50/70 border border-amber-100/70 rounded-xl p-3 flex items-center gap-3">
            <div className="w-6 h-5 bg-amber-200/80 rounded"></div>
            <div className="w-10 h-10 rounded-full bg-amber-200/80"></div>
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 bg-amber-200/80 rounded w-20"></div>
              <div className="h-2.5 bg-amber-200/60 rounded w-14"></div>
            </div>
            <div className="w-12 h-5 bg-amber-200/80 rounded-md"></div>
          </div>

          {/* Other Ranks List */}
          <div className="space-y-3 pt-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <div className="w-6 h-4 bg-slate-200 rounded text-center"></div>
                <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-slate-200 rounded w-24"></div>
                  <div className="h-2.5 bg-slate-100 rounded w-16"></div>
                </div>
                <div className="w-12 h-4 bg-slate-100 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default AchievementsSkeleton;
